import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { zodValidator } from "@tanstack/zod-adapter";
import { themes, countries, citiesByCountry, priceBuckets, type Photographer } from "@/data/photographers";
import { fetchAllPhotographers } from "@/lib/photographers-source";
import { PortraitImage } from "@/components/PortraitImage";
import { CreatorFilterBar } from "@/components/CreatorFilterBar";
import { creatorFiltersSchema, matchesCreatorFilters, type CreatorFilters } from "@/lib/creator-filters";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/photographers/")({
  validateSearch: zodValidator(creatorFiltersSchema),
  head: () => ({
    meta: [
      { title: "Fotografen, OM Selected" },
      { name: "description", content: "Verzeichnis aller Fotografen bei OM Selected. Filter nach Region, Thema und Preis." },
      { property: "og:title", content: "Fotografen, OM Selected" },
      { property: "og:description", content: "Kuratierte Fotografen, nach Region und Thema." },
      { property: "og:url", content: "https://omselected.art/photographers" },
    ],
    links: [{ rel: "canonical", href: "https://omselected.art/photographers" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Fotografen, OM Selected",
          description: "Verzeichnis aller Fotografen bei OM Selected. Filter nach Region, Thema und Preis.",
          url: "https://omselected.art/photographers",
          isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
        }),
      },
    ],
  }),
  component: PhotographersPage,
});

function PhotographersPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [photographers, setPhotographers] = useState<Photographer[]>([]);
  const [loading, setLoading] = useState(true);

  const updateFilters = (patch: Partial<CreatorFilters>) =>
    navigate({ search: (prev: CreatorFilters) => ({ ...prev, ...patch }), replace: true });

  const resetFilters = () =>
    navigate({
      search: { country: "", city: "", theme: "", price: "", setting: "", language: "", page: 1 },
      replace: true,
    });

  useEffect(() => {
    fetchAllPhotographers().then((list) => {
      setPhotographers(list);
      setLoading(false);
    });
  }, []);

  const cities = useMemo(() => {
    const base = !search.country
      ? []
      : citiesByCountry[search.country as keyof typeof citiesByCountry] ?? [];
    const fromData = photographers
      .filter((p) => !search.country || p.country === search.country)
      .map((p) => p.city ?? p.region)
      .filter((c): c is string => !!c && c !== "");
    return Array.from(new Set([...base, ...fromData])).sort();
  }, [search.country, photographers]);

  const filtered = photographers.filter((p) => matchesCreatorFilters(p, search, priceBuckets));

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-12 pb-16 relative">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div>
              <div className="eyebrow text-muted-foreground">{t("directory.eyebrow")}</div>
              <h1 className="serif text-5xl md:text-7xl mt-6 leading-[0.95]">{t("directory.photographersTitle")}</h1>
              <p className="mt-6 max-w-xl text-muted-foreground">
                {loading ? t("common.loading") : t("directory.photographersIntro", { count: photographers.length })}
              </p>
            </div>
            <div className="shrink-0">
              <Link
                to="/apply"
                className="inline-block bg-foreground text-background px-8 py-4 eyebrow hover:opacity-90 transition-opacity"
              >
                {t("directory.applyPhotographer")}
              </Link>
              <Link
                to="/fuer-fotografen"
                className="mt-3 block text-center eyebrow text-muted-foreground hover:text-foreground transition-colors"
              >
                Selbst gelistet werden →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-secondary/40">
        <CreatorFilterBar
          values={search}
          onChange={updateFilters}
          onReset={resetFilters}
          resultsCount={filtered.length}
          show={{
            country: countries.map((c) => ({ value: c, label: c })),
            city: cities.map((c) => ({ value: c, label: c })),
            theme: themes.map((t) => ({ value: t.id, label: t.label })),
            price: priceBuckets.map((b) => ({ value: b.id, label: b.label })),
            setting: true,
            language: true,
          }}
        />
      </section>

      <section>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <h2 className="sr-only">{t("directory.allPhotographers")}</h2>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-x-8">
            {filtered.map((p) => (
              <Link key={p.id} to="/photographers/$id" params={{ id: p.id }} className="block group cursor-pointer mb-16 break-inside-avoid">
                <PortraitImage p={p} />
                <div className="mt-4 flex justify-between items-baseline">
                  <h2 className="serif text-2xl">{p.name}</h2>
                  <div className="eyebrow text-muted-foreground">{p.city ?? p.region}{p.country ? `, ${p.country}` : ""}</div>
                </div>
                <div className="mt-2 eyebrow text-muted-foreground">
                  {p.themes.map((t) => themes.find((th) => th.id === t)?.label).join(" · ")}
                </div>
                {p.priceFrom > 0 && (
                  <div className="mt-2 text-sm text-muted-foreground">
                    {t("common.priceFrom", { price: p.priceFrom.toLocaleString("de-DE") })}
                  </div>
                )}
              </Link>
            ))}
          </div>
          {loading && photographers.length === 0 && (
            <div className="py-24 text-center text-muted-foreground italic">{t("common.loading")}</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="py-24 text-center text-muted-foreground italic">{t("directory.emptyPhotographers")}</div>
          )}
        </div>
      </section>
    </div>
  );
}